import type { RecordOfMatchesState } from '../matches/types';
import type { RecordOfSportsState, SSRSportsState } from './types';

import type { BackendEntities } from '../types/backendEntities';
import {
  fillStateWithMatchIds,
  removeMatches,
  transformNodeSSRState,
  updateField,
} from '../utils';
import { createSportEntity, initialState } from './records';
import { transformSport } from './transform';

// ---

// Receives a piece of state from server as a plain JS object and transforms into an immutable object.
export function transformSportsSSRState(state: SSRSportsState): RecordOfSportsState {
  return transformNodeSSRState(state, initialState, (entry) => {
    return createSportEntity({
      id: entry.id,
      name: entry.name,
      priority: entry.priority,
      matchesCount: entry.matchesCount,
      matchIds: new Set(entry.matchIds),
    });
  });
}

// Sport entities reducer.
function sportsReducer(
  state: RecordOfSportsState = initialState(),
  clientEntities: BackendEntities | undefined,
  matchesState: RecordOfMatchesState['byId'] | undefined,
): RecordOfSportsState {
  if (!clientEntities?.diff) {
    return state;
  }

  const { sports, matches, sportMatchesCount, removedMatches } = clientEntities.diff;

  if (!(sports || matches || sportMatchesCount || removedMatches)) {
    return state;
  }

  let sportsState = state.get('byId').asMutable();
  let sportIds = state.get('allIds');

  if (removedMatches && matchesState) {
    const newState = removeMatches('sportId', removedMatches, matchesState, sportsState, sportIds);

    sportsState = newState.state;
    sportIds = newState.stateIds;
  }

  // .asMutable() - i did it here, because the .delete() function works in Immutable.List() only in immutable mode
  sportIds = sportIds.asMutable();

  if (sports) {
    const sportsKeys = Object.keys(sports);

    sportsKeys.forEach((sportKey) => {
      const transformed = transformSport(sports[sportKey]);

      if (sportsState.has(transformed.id)) {
        if (transformed.name !== undefined) {
          sportsState = updateField(sportsState, transformed.id, 'name', transformed.name);
        }
        if (transformed.priority !== undefined) {
          sportsState = updateField(sportsState, transformed.id, 'priority', transformed.priority);
        }
      }
      else if (matches || sportMatchesCount) {
        sportIds = sportIds.push(transformed.id);
        sportsState = sportsState.set(transformed.id, createSportEntity(transformed));
      }
    });
  }

  if (matches || sportMatchesCount) {
    const newState = fillStateWithMatchIds('sportId', matches, sportMatchesCount, sportsState, sportIds);

    sportsState = newState.state;
    sportIds = newState.stateIds;
  }

  state = state.set('byId', sportsState.asImmutable());
  state = state.set('allIds', sportIds.asImmutable());

  return state;
}

export default sportsReducer;
